// Cómo se crean los videos de la tanda (ADR-2026-10-30-videos-cortos-con-el-agente): cada click en
// Crear es UN lote nuevo (`crypto.randomUUID()`) con los productos por lanzar, cada uno con su
// guion y su voz. El plan se marca ANTES de mandar (si la respuesta se pierde o la persona recarga a
// mitad, la sesión encuentra el lote y lo sigue), y lo que el backend no pudo encolar vuelve a
// "escrito" con el motivo. `seguirLotes` polea cada lote abierto cada 8 s (un video tarda ~5 min)
// y trae los videos listos firmados; un lote que no existe o que pasó los 30 minutos se suelta.
import {
  aplicarEstadoDelLote,
  fallarNoEncolados,
  lotesAbiertos,
  marcarLanzados,
  olvidarLote,
  soltarLotesVencidos,
  videosPorLanzar,
  vozDelProducto,
  type PlanDeVideos,
  type ProductoDeLaTanda,
} from './plan';
import { lanzarLote, verLote, videosListos, type ErrorDeVideosCortos, type VideoListo, type VideoParaElLote } from './videosCortosApi';

export type ResultadoDeGeneracion =
  | { ok: true; plan: PlanDeVideos; lanzados: number; loteId: string; recuperada?: boolean }
  | { ok: false; error: string; sinCreditos: boolean; plan: PlanDeVideos; lanzados: number };

/** Lo que viaja de cada producto al lote. Puro. */
export function videoParaElLote(plan: Pick<PlanDeVideos, 'voz'>, p: ProductoDeLaTanda): VideoParaElLote {
  return {
    variante_id: p.varianteId,
    ...(p.voz !== undefined ? { voz: vozDelProducto(plan, p) } : {}),
    guion: p.guion!,
    // Rehacer sólo la voz: se arma sobre el video que ya salió (ADR-2026-10-31-videos-cortos-rehacer-solo-la-voz).
    ...(p.rehacer && p.soloVoz && p.videoId ? { solo_voz_de: p.videoId } : {}),
  };
}

/**
 * Ya hay una tanda creándose (el backend dijo cuál) y la pantalla no la conoce: se perdió, p. ej.
 * por una recarga antes de que se guardara el plan. Se vuelven a marcar en vuelo los productos de
 * ESTE plan que están en esa tanda, y el seguimiento hace el resto (ADR-2026-10-30-videos-cortos-
 * recupera-la-tanda-en-curso). Devuelve el plan y cuántos recuperó (0 = la tanda es de otra sesión).
 */
export async function recuperarLote(plan: PlanDeVideos, loteEnCurso: string, ahora = Date.now()): Promise<{ plan: PlanDeVideos; recuperados: string[] }> {
  if (lotesAbiertos(plan).some((l) => l.loteId === loteEnCurso)) return { plan, recuperados: [] };
  let estado: Awaited<ReturnType<typeof verLote>>;
  try {
    estado = await verLote(loteEnCurso);
  } catch {
    return { plan, recuperados: [] };
  }
  const delLote = new Set((estado?.videos ?? []).flatMap((v) => (v.variante_id ? [v.variante_id] : [])));
  const ids = plan.productos.filter((x) => delLote.has(x.varianteId) && x.estado !== 'en_cola' && x.estado !== 'generando').map((x) => x.varianteId);
  if (ids.length === 0) return { plan, recuperados: [] };
  return { plan: marcarLanzados(plan, loteEnCurso, ids, ahora), recuperados: ids };
}

export async function generarVideos(p: {
  plan: PlanDeVideos;
  soloIds?: string[];
  onPlan: (plan: PlanDeVideos) => void;
  /**
   * Guarda el plan YA (sin el debounce del autoguardado). Se llama con los productos marcados en
   * vuelo, antes de mandar: si la página se recarga con el pedido en camino, el lote no se pierde.
   */
  guardarYa?: (plan: PlanDeVideos) => Promise<unknown>;
  nuevoLoteId?: () => string;
  /**
   * El plan VIVO: lo que se publica después de esperar la red se aplica sobre él, no sobre la copia de antes del pedido
   * (Greptile #233, ADR-2026-10-31-videos-cortos-revision-de-greptile). Sin esto, sobre la copia.
   */
  leerPlan?: () => PlanDeVideos | null;
}): Promise<ResultadoDeGeneracion> {
  const pedidos = p.soloIds?.length ? new Set(p.soloIds) : null;
  const productos = videosPorLanzar(p.plan).filter((x) => !pedidos || pedidos.has(x.varianteId));
  if (productos.length === 0) return { ok: false, error: 'No hay videos por crear.', sinCreditos: false, plan: p.plan, lanzados: 0 };
  const loteId = (p.nuevoLoteId ?? (() => crypto.randomUUID()))();
  const ids = productos.map((x) => x.varianteId);
  let plan = marcarLanzados(p.plan, loteId, ids, Date.now());
  p.onPlan(plan);
  const vivo = (): PlanDeVideos => p.leerPlan?.() ?? plan;
  await p.guardarYa?.(plan).catch(() => undefined);
  try {
    const r = await lanzarLote(
      loteId,
      productos.map((x) => videoParaElLote(plan, x)),
      { voz: plan.voz, subtitulos: plan.subtitulos, indicacion: plan.indicacion },
    );
    plan = vivo();
    if (r.noEncolados.length) {
      plan = fallarNoEncolados(plan, loteId, r.noEncolados, 'No entró en el lote. Tocá Crear para reintentarlo.');
      p.onPlan(plan);
    }
    window.dispatchEvent(new Event('atlas:credits-updated'));
    return { ok: true, plan, lanzados: ids.length - r.noEncolados.length, loteId };
  } catch (e) {
    const err = e as ErrorDeVideosCortos;
    const mensaje = err.message || 'No se pudieron crear los videos.';
    // Ya había una tanda creándose: si es de esta sesión, se la vuelve a seguir en vez de dar error.
    if (err.loteEnCurso) {
      const r = await recuperarLote(olvidarLote(vivo(), loteId), err.loteEnCurso);
      // Lo recuperado se marca sobre el plan de ahora (`recuperarLote` esperó la red).
      const sinElNuevo = olvidarLote(vivo(), loteId);
      plan = r.recuperados.length ? marcarLanzados(sinElNuevo, err.loteEnCurso, r.recuperados, Date.now()) : sinElNuevo;
      p.onPlan(plan);
      if (r.recuperados.length > 0) {
        await p.guardarYa?.(plan).catch(() => undefined);
        const resto = ids.filter((id) => !r.recuperados.includes(id)).length;
        return resto > 0
          ? { ok: false, error: 'Ya había un lote creándose: lo seguimos acá. Cuando termine vas a poder crear el resto.', sinCreditos: false, plan, lanzados: 0 }
          : { ok: true, plan, lanzados: 0, loteId: err.loteEnCurso, recuperada: true };
      }
      return { ok: false, error: mensaje, sinCreditos: false, plan, lanzados: 0 };
    }
    // ¿Se llegó a crear algo? `null` = el backend confirma que no; un error al preguntar = no se sabe (la conexión
    // pudo caerse después de que el pedido entró), y entonces se sigue el lote en vez de olvidarlo.
    let creada: Awaited<ReturnType<typeof verLote>> | undefined;
    try {
      creada = await verLote(loteId);
    } catch {
      creada = undefined;
    }
    if (creada === null || (creada && !creada.videos.length)) {
      // La tanda entera no arrancó: el error se avisa una vez (el aviso de la pantalla), no en cada tarjeta.
      plan = olvidarLote(vivo(), loteId);
      p.onPlan(plan);
      return { ok: false, error: mensaje, sinCreditos: err.status === 402 || err.tipo === 'billing', plan, lanzados: 0 };
    }
    const dentro = new Set((creada?.videos ?? []).map((v) => v.variante_id));
    const afuera = creada ? ids.filter((id) => !dentro.has(id)) : [];
    plan = vivo();
    if (afuera.length) plan = fallarNoEncolados(plan, loteId, afuera, 'No entró en el lote. Tocá Crear para reintentarlo.');
    p.onPlan(plan);
    return {
      ok: false,
      error: !creada ? 'No pudimos confirmar si el lote arrancó. Lo estamos siguiendo.' : `El lote arrancó, pero ${afuera.length === 1 ? 'un video no entró' : `${afuera.length} videos no entraron`}. Tocá Crear para reintentarlos.`,
      sinCreditos: false,
      plan,
      lanzados: ids.length - afuera.length,
    };
  }
}

/** Cada cuánto se mira el estado de la tanda. Un video tarda ~5 min: no hace falta más seguido. */
export const CADA_MS = 8_000;
const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((r) => {
    const t = setTimeout(r, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      r();
    });
  });

/**
 * Cuánto se espera a que el backend conozca un lote recién lanzado. El seguimiento arranca apenas
 * el plan marca el lote (antes de mandar el pedido, para no perderlo con una recarga), y el backend
 * crea las filas mientras atiende el pedido: un 404 en ese rato es "todavía no llegó", no "no
 * existe" (ADR-2026-10-31-videos-cortos-no-suelta-el-lote-recien-lanzado). Si el pedido falla,
 * `generarVideos` suelta el lote por su cuenta.
 */
export const GRACIA_DEL_LOTE_NUEVO_MS = 2 * 60_000;

type CambioDelPlan = (plan: PlanDeVideos) => PlanDeVideos;

/**
 * Una vuelta de seguimiento: consulta los lotes abiertos de `plan` y devuelve los CAMBIOS, para aplicarlos sobre el
 * plan vivo DESPUÉS de esperar la red. Aplicarlos sobre la copia leída antes pisaba lo que la persona tocó mientras
 * tanto (Greptile #233, ADR-2026-10-31-videos-cortos-revision-de-greptile).
 */
export async function cambiosDeLosLotes(plan: PlanDeVideos, companyId: string, ahora = Date.now()): Promise<CambioDelPlan> {
  const cambios: CambioDelPlan[] = [(p) => soltarLotesVencidos(p, ahora)];
  for (const lote of lotesAbiertos(soltarLotesVencidos(plan, ahora))) {
    let estado: Awaited<ReturnType<typeof verLote>>;
    try {
      estado = await verLote(lote.loteId);
    } catch {
      continue;
    }
    if (!estado) {
      if (ahora - lote.lanzadoEn < GRACIA_DEL_LOTE_NUEVO_MS) continue;
      cambios.push((p) => olvidarLote(p, lote.loteId, 'No encontramos este lote. Podés volver a crear el video.'));
      continue;
    }
    let listos: VideoListo[] = [];
    if (estado.listos > 0) {
      try {
        listos = await videosListos(lote.loteId, companyId);
      } catch {
        listos = [];
      }
    }
    const visto = estado;
    cambios.push((p) => aplicarEstadoDelLote(p, visto, listos));
  }
  return (vivo) => cambios.reduce((acc, cambio) => cambio(acc), vivo);
}

/** Una vuelta de seguimiento sobre `plan`, aplicada sobre ese mismo plan (lo usan los tests). */
export async function mirarLotes(plan: PlanDeVideos, companyId: string, ahora = Date.now()): Promise<PlanDeVideos> {
  return (await cambiosDeLosLotes(plan, companyId, ahora))(plan);
}

/**
 * Sigue los lotes abiertos hasta que no quede ninguno o se aborte. `leerPlan` da el plan VIVO (la persona pudo tocar
 * otras cosas mientras tanto) y `onPlan` publica cada vuelta.
 */
export async function seguirLotes(p: { leerPlan: () => PlanDeVideos | null; companyId: string; onPlan: (plan: PlanDeVideos) => void; signal: AbortSignal }): Promise<void> {
  while (!p.signal.aborted) {
    const plan = p.leerPlan();
    if (!plan || lotesAbiertos(plan).length === 0) return;
    const aplicar = await cambiosDeLosLotes(plan, p.companyId);
    if (p.signal.aborted) return;
    // Sobre el plan de AHORA: lo que se tocó durante la consulta sigue ahí.
    const vivo = p.leerPlan();
    if (!vivo) return;
    const nuevo = aplicar(vivo);
    if (nuevo !== vivo) p.onPlan(nuevo);
    if (lotesAbiertos(nuevo).length === 0) return;
    await sleep(CADA_MS, p.signal);
  }
}
